<template>
  <div class="ds-page">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Wage Payment System / Invoices</div>
          <h2 class="page-title">Invoice Items</h2>
          <p class="page-subtitle">
            Review invoice item breakdown for
            <strong>{{ invoiceNumber }}</strong>
            <span v-if="companyClientName !== 'selected client'">({{ companyClientName }})</span>
          </p>
        </div>
        <div class="col-auto ms-auto">
          <button @click="goBack" class="ds-btn ds-btn--secondary">
            <font-awesome-icon icon="arrow-left" />
            Back to Invoices
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body wps-invoice-detail__meta">
        <div class="wps-invoice-detail__meta-item">
          <span class="wps-invoice-detail__meta-label">Invoice Number</span>
          <span class="wps-invoice-detail__meta-value">{{ invoiceNumber }}</span>
        </div>
        <div class="wps-invoice-detail__meta-item">
          <span class="wps-invoice-detail__meta-label">Company</span>
          <span class="wps-invoice-detail__meta-value">{{ companyName }}</span>
        </div>
        <div class="wps-invoice-detail__meta-item">
          <span class="wps-invoice-detail__meta-label">Company Client</span>
          <span class="wps-invoice-detail__meta-value">{{ companyClientName }}</span>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="is_loading">
          <thead>
            <tr>
              <th>Item ID</th>
              <th>WPS Request ID</th>
              <th>Request Item ID</th>
              <th>Employee ID</th>
              <th class="text-right">Amount</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-bold">#{{ item.id }}</span>
              </td>
              <td>{{ item.wps_request_id || "—" }}</td>
              <td>{{ item.wps_request_item_id || "—" }}</td>
              <td>{{ item.employee_id || "—" }}</td>
              <td class="text-right">
                <span class="ds-amount">{{ item.amount_str }}</span>
              </td>
              <td>{{ item.created_at_str }}</td>
              <td>{{ item.updated_at_str }}</td>
            </tr>
          </tbody>
        </table>

        <el-empty
          v-if="!total_rows && !is_loading"
          description="No invoice items found"
          :image="emptyStateImage"
          class="ds-empty"
        />
      </div>

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { DETAIL_WPS_INVOICE_PER_ITEM } from "@/store/modules/wps";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import Pagination from "@/ui/components/Pagination.vue";

export default {
  name: "WagePaymentSystemInvoiceDetail",
  components: { Pagination },
  metaInfo: {
    title: "WPS Invoice Items",
  },
  computed: {
    invoiceNumber() {
      return this.$route.query.invoice_number || `Invoice #${this.$route.params.invoice_id}`;
    },
    companyName() {
      return this.$route.query.company_name || "selected company";
    },
    companyClientName() {
      return this.$route.query.company_client_name || "selected client";
    },
  },
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      page: 1,
      per_page: 20,
      total_rows: 0,
      list: [],
    };
  },
  mounted() {
    this.getList();
  },
  methods: {
    getApiNamespace() {
      return this.$route.path.startsWith("/wage-payment-system") ? "wage-payment-system" : "wps";
    },
    goBack() {
      this.$router.push({
        name: "Wage Payment System Invoice",
        params: {
          id: this.$route.params.id,
        },
        query: {
          company_name: this.companyName,
        },
      });
    },
    formatCurrency(value) {
      return `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const response = await this.$store.dispatch(DETAIL_WPS_INVOICE_PER_ITEM, {
          id: this.$route.params.invoice_id,
          api_namespace: this.getApiNamespace(),
          page: this.page,
          size: this.per_page,
          per_page: this.per_page,
        });

        const payload = response?.data || response || {};
        const rows = Array.isArray(payload.rows) ? payload.rows : [];

        this.list = rows.map((item) => ({
          ...item,
          amount_str: this.formatCurrency(item.amount),
          created_at_str: item.created_at ? moment(item.created_at).format("DD MMM YYYY, HH:mm") : "—",
          updated_at_str: item.updated_at ? moment(item.updated_at).format("DD MMM YYYY, HH:mm") : "—",
        }));
        this.total_rows = payload.count || 0;
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
  },
};
</script>

<style scoped>
.wps-invoice-detail__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: center;
}

.wps-invoice-detail__meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wps-invoice-detail__meta-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ds-text-hint);
}

.wps-invoice-detail__meta-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-base);
}

@media (max-width: 991px) {
  .wps-invoice-detail__meta {
    grid-template-columns: 1fr;
  }
}
</style>
